import { cn } from '@newfront-insurance/core-ui/v2';
import type { ReactNode } from 'react';

import { useChatInputContext } from './chat-input-context';
import { useDragHandlersRef } from './chat-input-context';

interface ChatInputProps {
  children: ReactNode;
  className?: string;
}

/**
 * Root wrapper component for chat input.
 * Provides the form container and drag/drop handlers.
 * Handlers come from MessagePayloadProvider (populated by ChatAttachments if rendered).
 *
 * @example
 * ```tsx
 * <ChatInput>
 *   <ChatInputTextarea />
 *   <ChatInputToolbar>
 *     <ChatAttachments />
 *     <SendButton />
 *   </ChatInputToolbar>
 * </ChatInput>
 * ```
 */
export function ChatInput({ children, className }: ChatInputProps) {
  const context = useChatInputContext();
  const { handleSubmit } = context;

  // Read drag handlers from ref (populated by ChatAttachments if rendered)
  const handlersRef = useDragHandlersRef();
  const handlers = handlersRef.current;

  const isDragging = handlers.isDragging ?? false;
  const handleDragOver = handlers.handleDragOver ?? (() => {});
  const handleDragLeave = handlers.handleDragLeave ?? (() => {});
  const handleDrop = handlers.handleDrop ?? (() => {});

  return (
    <div className={cn('flex-shrink-0 px-2', className)}>
      <form
        className="mx-auto flex w-full max-w-3xl gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <div
          className={cn('group relative w-full rounded-lg p-[1px]', {
            'bg-transparent': isDragging,
          })}
          style={{
            background: 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)',
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex w-full flex-col gap-0 overflow-hidden rounded-lg bg-white">{children}</div>
        </div>
      </form>
    </div>
  );
}
